import React from 'react';
import { Slider } from '@ui-component-library';
import * as Opt from '../editor/Helpers';
import MediaSelector from '../editor/fields/MediaSelector';
import Avatar from '../commons/Avatar';
import ColorSelector from '../editor/fields/ColorSelector';
import TextEditor from '../editor/fields/TextEditor';

const NUMTESTIMONIALSMOBILE = 8;

// Tailwind text sizes from text-sizes-mapping: h4, fs-5
const h4Classes = 'text-[25px] leading-[1.1] tracking-[-0.75px] md:text-[49px] md:leading-[1.05] md:tracking-[-1.47px]';
const fs5Classes = 'text-[20px] leading-[1.2] tracking-[-0.4px]';

const DESKTOP_VISIBLE = 2;

function TestimonialSlide({ item, showAvatarImage, showCompanyImage, hideAvatarOnlyMobile, isMobile, className = '' }) {
  return (
    <div className={`m-0 border-0 bg-transparent rounded-2xl p-0 pt-4 min-w-0 ${className}`}>
      <div className="block quill-text mb-[30px] w-full" dangerouslySetInnerHTML={{ __html: item.testimonial }} />
      <Avatar
        showAvatarImage={isMobile ? true : showAvatarImage}
        showCompanyImage={isMobile ? true : showCompanyImage}
        hideAvatarOnlyMobile={hideAvatarOnlyMobile}
        avatarSrc={item.avatarSrc}
        avatarAlt={item.avatarAlt}
        name={item.name}
        nameColor={item.nameColor}
        company={item.company}
        borderColor={item.borderColor}
        imageSrc={item.imageSrc}
        imageAlt={item.imageAlt}
      />
    </div>
  );
}

class TestimonialWithImage extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      activeItem: 0,
      activeGroup: 0,
    };
    this.mobileSliderRef = React.createRef();
    this.desktopSliderRef = React.createRef();
  }

  handleNextMobile = () => {
    this.mobileSliderRef.current?.next();
  };

  handlePrevMobile = () => {
    this.mobileSliderRef.current?.prev();
  };

  handleNextDesktop = () => {
    const nextPage = Math.min(Math.ceil(this.props.items.length / DESKTOP_VISIBLE) - 1, this.state.activeGroup + 1);
    this.desktopSliderRef.current?.scrollTo(nextPage * DESKTOP_VISIBLE);
  };

  handlePrevDesktop = () => {
    const prevPage = Math.max(0, this.state.activeGroup - 1);
    this.desktopSliderRef.current?.scrollTo(prevPage * DESKTOP_VISIBLE);
  };

  render() {
    const visibilityClasses = window.location.pathname.includes('editor') && ['dt', 'mob'].filter((i) => i === this.props.visibility).length
      ? 'opacity-50 flex'
      : this.props.visibility === 'dt'
        ? 'hidden lg:flex'
        : this.props.visibility === 'mob'
          ? 'block lg:hidden'
          : 'flex';

    return (
      <div className={`${visibilityClasses} p-0 bg-${this.props.backgroundColor}`}>
        <div className="container">
          <div className="w-full lg:w-2/3">
            <h4 className={`${h4Classes} break-words ${this.props.headerColor} mb-[10px]`}>{this.props.header}</h4>
            <p className={`${fs5Classes} mb-0 ${this.props.bodyColor}`}>{this.props.body}</p>
          </div>

          {/* Mobile Slider */}
          <div className="w-full block lg:hidden">
            <Slider
              ref={this.mobileSliderRef}
              mobileVisible={1}
              desktopVisible={1}
              onIndexChange={(index) => this.setState({ activeItem: index })}
            >
              {this.props.items.slice(0, NUMTESTIMONIALSMOBILE).map((item, index) => (
                <TestimonialSlide
                  key={index}
                  item={item}
                  hideAvatarOnlyMobile={this.props.hideAvatarOnlyMobile}
                  isMobile
                />
              ))}
            </Slider>

            {this.props.items.length > 1 && (
              <div className="w-full flex justify-center items-center mt-[20px]">
                <div className="rounded-full border-2 border-black flex mr-2 items-center justify-center w-12 h-12 cursor-pointer" role="button" onClick={this.handlePrevMobile}>
                  <img src={`${Window.static_path}img/icons/arrowBlack.svg`} height="16px" className="neutral-charcoal-filter" alt="Previous" />
                </div>
                {this.props.items.slice(0, NUMTESTIMONIALSMOBILE).map((_, index) => (
                  <div
                    key={index}
                    role="button"
                    tabIndex={0}
                    onClick={() => this.mobileSliderRef.current?.scrollTo(index)}
                    className={`border-2 border-neutral-charcoal rounded-full mx-2 transition-[.6s] cursor-pointer h-2.5 w-2.5 ${this.state.activeItem === index ? 'bg-neutral-charcoal' : ''}`}
                  />
                ))}
                <div className="rounded-full border-2 border-black flex ml-2 items-center justify-center w-12 h-12 cursor-pointer" role="button" onClick={this.handleNextMobile}>
                  <img src={`${Window.static_path}img/icons/arrowBlack.svg`} height="16px" className="neutral-charcoal-filter" alt="Next" style={{ transform: 'rotate(180deg)' }} />
                </div>
              </div>
            )}
          </div>

          {/* Desktop Slider - 2 items per slide */}
          <div className="mt-[20px] w-full hidden lg:block">
            <Slider
              ref={this.desktopSliderRef}
              mobileVisible={1}
              desktopVisible={DESKTOP_VISIBLE}
              onIndexChange={(index) => this.setState({ activeGroup: Math.floor(index / DESKTOP_VISIBLE) })}
            >
              {this.props.items.map((item, index) => (
                <TestimonialSlide
                  key={index}
                  item={item}
                  showAvatarImage={this.props.showAvatarImage}
                  showCompanyImage={this.props.showCompanyImage}
                  hideAvatarOnlyMobile={this.props.hideAvatarOnlyMobile}
                  isMobile={false}
                  className="flex-1 min-h-0"
                />
              ))}
            </Slider>

            <div className="mt-3 flex items-center justify-between">
              <div className="flex justify-start">
                {Array.from({ length: Math.ceil(this.props.items.length / DESKTOP_VISIBLE) }).map((_, pageIndex) => (
                  <div
                    key={pageIndex}
                    role="button"
                    tabIndex={0}
                    onClick={() => this.desktopSliderRef.current?.scrollTo(pageIndex * DESKTOP_VISIBLE)}
                    className={`border-2 border-neutral-charcoal rounded-full mx-[10px] h-4 w-4 transition-[.6s] cursor-pointer ${this.state.activeGroup === pageIndex ? 'bg-neutral-charcoal' : ''}`}
                  />
                ))}
              </div>

              {Math.ceil(this.props.items.length / DESKTOP_VISIBLE) > 1 && (
                <div className="flex justify-end">
                  <div className="rounded-full border-2 border-black flex mr-2 items-center justify-center w-12 h-12 cursor-pointer" role="button" onClick={this.handlePrevDesktop}>
                    <img src={`${Window.static_path}img/icons/arrowBlack.svg`} height="16px" className="neutral-charcoal-filter" alt="Previous" />
                  </div>
                  <div className="rounded-full border-2 border-neutral-charcoal flex ml-2 items-center justify-center w-12 h-12 cursor-pointer" role="button" onClick={this.handleNextDesktop}>
                    <img src={`${Window.static_path}img/icons/arrowBlack.svg`} height="16px" className="neutral-charcoal-filter" alt="Next" style={{ transform: 'rotate(180deg)' }} />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

TestimonialWithImage.defaultProps = {
  visibility: "",
  showAvatarImage: true,
  showCompanyImage: true,
  hideAvatarOnlyMobile: false,
  backgroundColor: 'white-50',
  header: 'Customer testimonials',
  headerColor: 'neutral-charcoal',
  body: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit.',
  bodyColor: 'neutral-charcoal',
  text: "Action",
  href: "/",
  variant: "outline",
  iconAfter: null,
  iconColor: "neutral-charcoal-filter",
  items: [
    {
      testimonial: '"ONE Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse varius enim in eros elementum tristique. Duis cursus, mi quis viverra ornare, eros dolor interdum nulla, ut commodo diam libero vitae erat."',
      avatarSrc: "https://placehold.co/600x400",
      avatarAlt: "scenic image",
      name: 'Name Surname',
      nameColor: 'neutral-charcoal',
      company: 'Position, Company name',
      companyColor: 'neutral-charcoal',
      borderColor: 'neutral-charcoal',
      imageSrc: "https://placehold.co/600x400",
      imageAlt: "scenic image",
    },
    {
      testimonial: '"TWO Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse varius enim in eros elementum tristique. Duis cursus, mi quis viverra ornare, eros dolor interdum nulla, ut commodo diam libero vitae erat."',
      avatarSrc: "https://placehold.co/600x400",
      avatarAlt: "scenic image",
      name: 'Name Surname',
      nameColor: 'neutral-charcoal',
      company: 'Position, Company name',
      companyColor: 'neutral-charcoal',
      borderColor: 'neutral-charcoal',
      imageSrc: "https://placehold.co/600x400",
      imageAlt: "scenic image",
    },
    {
      testimonial: '"THREE Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse varius enim in eros elementum tristique. Duis cursus, mi quis viverra ornare, eros dolor interdum nulla, ut commodo diam libero vitae erat."',
      avatarSrc: "https://placehold.co/600x400",
      avatarAlt: "scenic image",
      name: 'Name Surname',
      nameColor: 'neutral-charcoal',
      company: 'Position, Company name',
      companyColor: 'neutral-charcoal',
      borderColor: 'neutral-charcoal',
      imageSrc: "https://placehold.co/600x400",
      imageAlt: "scenic image",
    },
    {
      testimonial: '"FOUR Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse varius enim in eros elementum tristique. Duis cursus, mi quis viverra ornare, eros dolor interdum nulla, ut commodo diam libero vitae erat."',
      avatarSrc: "https://placehold.co/600x400",
      avatarAlt: "scenic image",
      name: 'Name Surname',
      nameColor: 'neutral-charcoal',
      company: 'Position, Company name',
      companyColor: 'neutral-charcoal',
      borderColor: 'neutral-charcoal',
      imageSrc: "https://placehold.co/600x400",
      imageAlt: "scenic image",
    },
    {
      testimonial: '"FIVE Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse varius enim in eros elementum tristique. Duis cursus, mi quis viverra ornare, eros dolor interdum nulla, ut commodo diam libero vitae erat."',
      avatarSrc: "https://placehold.co/600x400",
      avatarAlt: "scenic image",
      name: 'Name Surname',
      nameColor: 'neutral-charcoal',
      company: 'Position, Company name',
      companyColor: 'neutral-charcoal',
      borderColor: 'neutral-charcoal',
      imageSrc: "https://placehold.co/600x400",
      imageAlt: "scenic image",
    },
    {
      testimonial: '"SIX Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse varius enim in eros elementum tristique. Duis cursus, mi quis viverra ornare, eros dolor interdum nulla, ut commodo diam libero vitae erat."',
      avatarSrc: "https://placehold.co/600x400",
      avatarAlt: "scenic image",
      name: 'Name Surname',
      nameColor: 'neutral-charcoal',
      company: 'Position, Company name',
      companyColor: 'neutral-charcoal',
      borderColor: 'neutral-charcoal',
      imageSrc: "https://placehold.co/600x400",
      imageAlt: "scenic image",
    },
    {
      testimonial: '"SEVEN Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse varius enim in eros elementum tristique. Duis cursus, mi quis viverra ornare, eros dolor interdum nulla, ut commodo diam libero vitae erat."',
      avatarSrc: "https://placehold.co/600x400",
      avatarAlt: "scenic image",
      name: 'Name Surname',
      nameColor: 'neutral-charcoal',
      company: 'Position, Company name',
      companyColor: 'neutral-charcoal',
      borderColor: 'neutral-charcoal',
      imageSrc: "https://placehold.co/600x400",
      imageAlt: "scenic image",
    },
    {
      testimonial: '"EIGHT Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse varius enim in eros elementum tristique. Duis cursus, mi quis viverra ornare, eros dolor interdum nulla, ut commodo diam libero vitae erat."',
      avatarSrc: "https://placehold.co/600x400",
      avatarAlt: "scenic image",
      name: 'Name Surname',
      nameColor: 'neutral-charcoal',
      company: 'Position, Company name',
      companyColor: 'neutral-charcoal',
      borderColor: 'neutral-charcoal',
      imageSrc: "https://placehold.co/600x400",
      imageAlt: "scenic image",
    },
  ]

}

TestimonialWithImage.fields = {
  showAvatarImage:{
    type: "radio",
    options:[
        {label: "No", value: false},
        {label: "Yes", value: true}
    ]
  },
  showCompanyImage:{
    type: "radio",
    options:[
        {label: "No", value: false},
        {label: "Yes", value: true}
    ]
  },
  hideAvatarOnlyMobile:{
    type: "radio",
    options:[
        {label: "No", value: false},
        {label: "Yes", value: true}
    ]
  },
  backgroundColor: {
    type: "custom",
    render: (props) => <ColorSelector {...props} />
  },
  visibility: {
    type: "select",
    options: Opt.visibilityOptions
  },
  header: { type: "text" },
  headerColor: {
    type: "select",
    options: Opt.colorVariables
  },
  body: { type: "text" },
  bodyColor: {
    type: "select",
    options: Opt.colorVariables
  },
  text: { type: "text" },
  href: { type: "text" },
  variant: { type: "select", options: Opt.buttonOptions },
  iconAfter: {
    type: "custom",
    render: ({ name, onChange, value }) => <MediaSelector name={'iconAfter'} type={'icon'} onChange={onChange} value={value} />,
  },
  iconColor: {
    type: "select",
    options: Opt.filterOptions
  },
  items: {
    type: "array",
    arrayFields: {
      testimonial: { 
        type: "custom",
        render: ({ name, onChange, value }) => <TextEditor name={name} value={value} onChange={onChange} /> 
      },
      avatarSrc: {
        type: "custom",
        render: ({ name, onChange, value }) => <MediaSelector name={name} type={'img'} onChange={onChange} value={value} />,
      },
      avatarAlt: {
        type: "text"
      },
      name: { type: "text" },
      nameColor: {
        type: "select",
        options: Opt.colorVariables
      },
      company: { type: "text" },
      companyColor: {
        type: "select",
        options: Opt.colorVariables
      },
      borderColor: {
        type: "select",
        options: Opt.colorVariables
      },
      imageSrc: {
        type: "custom",
        render: ({ name, onChange, value }) => <MediaSelector name={name} type={'img'} onChange={onChange} value={value} />,
      },
      imageAlt: {
        type: "text"
      },
    },
    defaultItemProps: TestimonialWithImage.defaultProps.items[0],
  },
}

export default TestimonialWithImage;